import type { FunctionComponent, PropsWithChildren } from 'react'
import React from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { defineFormatters } from '@app/utils/reactIntl'
import { useAppDispatch } from '@app/utils/redux'

import { analyticsEvent } from '@app/packages/analytics/declaration'
import { useSchemeContext } from '@app/packages/SchemeContext/SchemeContext'
import { useThunkSelector } from '@app/packages/thunkSelector/useThunkSelector'

import { useEvent } from '@app/hooks/useEvent'

import { handleCallbackAction } from '@app/store/actions/ui'
import { hasCookieConsentSelector, provideConsent } from '@app/store/slices/cookies'

import { Button } from '@app/components/Button/Button'
import { ButtonTypeTertiaryOK } from '@app/components/Button/ButtonType'
import { ControlClasses } from '@app/components/ControlClasses/ControlClasses'

import Typography from '@app/components/Typography/Typography.module.scss'
import classes from './CookieBanner.module.scss'

export const CookieBanner: FunctionComponent = () => {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()

  const scheme = useSchemeContext().scheme

  const handleConsent = useEvent(() => {
    void dispatch(handleCallbackAction(provideConsent(String(formatMessage(messages.text, defineFormatters({ link: msgs => msgs }))), 'cookie_banner'), true))
  })

  return (
    <div className={cn(classes.root, classes[`scheme_${scheme}`], Typography.decorate_text)}>
      <div>
        {formatMessage(
          messages.text,
          defineFormatters({
            link: chunks => (
              <a href="/cookie-policy" target="_blank">
                {chunks}
              </a>
            ),
            b: chunks => <b>{chunks}</b>,
          })
        )}
      </div>
      <Button
        action={{
          event: analyticsEvent(() => ({
            // Accepted the cookie banner, which is what dismisses it.
            type: 'cookie-banner.agree.click',
          })),
          handler: handleConsent,
        }}
        className={ControlClasses.no_flex}
        type={ButtonTypeTertiaryOK.getInstance({ size: 'small', fontSize: 'small' })}
      >
        {formatMessage(messages.agree)}
      </Button>
    </div>
  )
}

export const CookieBannerOverlay: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const hasConsent = useThunkSelector(hasCookieConsentSelector).suspend()

  return (
    <React.Fragment>
      {children}
      {!hasConsent && (
        <div className={classes.overlay}>
          <CookieBanner />
        </div>
      )}
    </React.Fragment>
  )
}

const messages = defineMessages({
  text: { id: "components.CookieBanner.CookieBanner.messages.text.1462", defaultMessage: 'Kidsout обрабатывает cookies с целью персонализации сервисов и чтобы пользоваться веб-сайтом было удобнее. Вы можете запретить обработку сookies в настройках браузера. Пожалуйста, ознакомьтесь с <link>политикой использования cookies</link>.' },
  agree: { id: "components.CookieBanner.CookieBanner.messages.agree.1807", defaultMessage: 'Принимаю' },
})
